import React, { useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import I18n, { useT } from '@meta-react/i18n'
import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import {
  Typography,
  Avatar,
  ListItemAvatar,
  ListItemText
} from '@material-ui/core'
import ImageIcon from '@material-ui/icons/Image'

import { useDialog } from '@galo-monitor/dialog'
import { onoff } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { AuthenticationActions } from '@galo-monitor/stores/authentication'
import {
  selectCurrentUserDefaultProperty,
  selectUser
} from '@galo-monitor/stores/authentication/selectorAuthentication'
import { ContextActions } from '@galo-monitor/stores/context'
import { PropertyActions } from '@galo-monitor/stores/property'
import logo from '@galo-monitor/styles/assets/images/galomonitor-logo.svg'
import colors from '@galo-monitor/styles/colors'
import Button from '@galo-monitor/web-components/Button'
import Icon from '@galo-monitor/web-components/Icon'
import IconButton from '@galo-monitor/web-components/IconButton'
import CircularLoader from '@galo-monitor/web-components/Loader/CircularLoader'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import PropertyModal from '@galo-monitor/web-containers/modals/farm/PropertyModal'

import {
  Container,
  LinkStyledText,
  ListProperty,
  ListItemProperty,
  MessageContainer,
  TopRightButtonContainer
} from './theme'

function SelectPropertiesScreen({ accessType }) {
  const dispatch = useDispatch()
  const { createDialog } = useDialog()
  const t = useT()

  const { data: currentFarmProperties, error: currentFarmPropertiesError } = useGaloMonitorApi('/farms/properties')

  const {
    isFarm,
    isCompleteFirstFarm
  } = useSelector(selectUser)
  const userDefaultProperty = useSelector(selectCurrentUserDefaultProperty)

  const isLoadingProperties = useMemo(() => !currentFarmProperties && !currentFarmPropertiesError, [currentFarmProperties, currentFarmPropertiesError])

  const onErrorSelectingProperty = useCallback(
    () => createDialog({
      id: 'change-property-error-modal',
      Component: ErrorModal,
      props: {
        title: `${ t('could not {action} the {this}', {
          howMany: 1,
          gender: 'female',
          action: toLower(t('change')),
          this: toLower(t('property', { howMany: 1 }))
        })  }.`,
        message: t('try again contact support')
      }
    }),
    [createDialog, t]
  )

  const handlePropertyClickSelection = useCallback(property => {
    dispatch(PropertyActions.setDefaultProperty(
      property,
      () => {
        dispatch(AuthenticationActions.setUserDefaultProperty({
          id: property.id,
          name: property.name
        }))
        dispatch(ContextActions.initContextProperty())
        dispatch(PropertyActions.setCurrentProperty(property))
      },
      onErrorSelectingProperty
    ))
  }, [dispatch, onErrorSelectingProperty])

  let screenContent = <></>

  switch(accessType.status) {
    case 'inactive':
      screenContent = (
        <>
          <MessageContainer>
            <Typography variant="h6" color="textSecondary">
              {t(accessType.message, { screen: 'inside', message: 'first' })}.
            </Typography>
            <Typography variant="h6" color="textSecondary">
              {t(accessType.message, { screen: 'inside', message: 'second' })}.
            </Typography>
          </MessageContainer>
          <LinkStyledText
            color="error"
            onClick={ () => dispatch(AuthenticationActions.logout()) }
          >
            {t('exit {this}', { gender: 'male', this: t('system') })}
          </LinkStyledText>
        </>
      )
      break
    case 'full':
      screenContent = !isCompleteFirstFarm && isFarm ? (
        <>
          <MessageContainer>
            <Typography variant="h6" color="textSecondary">
              <I18n>first access message</I18n>
            </Typography>
            <Typography variant="h6" color="textSecondary">
              <I18n>register first property message</I18n>
            </Typography>
          </MessageContainer>
          <Button
            id="register-property-button"
            onClick={
              () => createDialog({
                id: 'finish-first-property-registration',
                Component: PropertyModal,
                props: {
                  property: userDefaultProperty,
                  onSuccess: () => {
                    dispatch(AuthenticationActions.setCompletePropertyRegistration())
                  }
                }
              })
            }
          >
            {`${ t('register') } ${ t('property', { howMany: 1 }) }`}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h6" color="textSecondary">
            <I18n>select desired property</I18n>
          </Typography>
          <ListProperty>
            {currentFarmProperties?.map(property => (
              <ListItemProperty
                key={ property.id }
                onClick={ () => handlePropertyClickSelection(property) }
                button
              >
                <ListItemAvatar>
                  <Avatar>
                    <ImageIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={ <Typography variant="h6" color="textPrimary">{property.name}</Typography> }
                  secondary={ <Typography color="textSecondary">{property.city} - {property.state}</Typography> }
                />
              </ListItemProperty>
            ))}
          </ListProperty>
          { isLoadingProperties ? <CircularLoader /> : null }
        </>
      )
      break
    default:
      screenContent = <></>
  }

  return (
    <>
      <TopRightButtonContainer>
        <IconButton
          tooltip={ t('logout') }
          onClick={ () => dispatch(AuthenticationActions.logout()) }
        >
          <Icon icon={ onoff } color={ colors.mutedText } />
        </IconButton>
      </TopRightButtonContainer>
      <Container>
        <img
          alt="Galo Monitor"
          src={ logo }
          width={ 55 }
          loading="lazy"
        />
        {screenContent}
      </Container>
    </>
  )
}

SelectPropertiesScreen.propTypes = {
  accessType: PropTypes.object.isRequired
}

export default SelectPropertiesScreen
